import { createContext, forwardRef, ReactNode, useCallback, useImperativeHandle, useMemo, useRef } from 'react'
import { toast } from 'react-toastify'
import { t } from 'i18next'
import { FormActions } from '@lovoldsystem/core.client/form-schema-v2'
import { updateChecklistCompletionData } from '../../http'

export interface WorkOrderChecklistValidationResult {
  completionId: EntityId
  isValid: boolean
  answers: string | null
}

export interface WorkOrderChecklistRefsRegistry {
  register: (completionId: EntityId, ref: FormActions | null) => void
}

export interface WorkOrderChecklistRefsHandle {
  validateAll: () => WorkOrderChecklistValidationResult[]
}

export const WorkOrderChecklistRefsContext = createContext<WorkOrderChecklistRefsRegistry | null>(null)

export const WorkOrderChecklistRefsProvider = forwardRef<WorkOrderChecklistRefsHandle, { children: ReactNode }>(
  ({ children }, ref) => {
    const refsById = useRef(new Map<EntityId, FormActions>())

    const register = useCallback((completionId: EntityId, formActions: FormActions | null) => {
      if (formActions) refsById.current.set(completionId, formActions)
      else refsById.current.delete(completionId)
    }, [])

    useImperativeHandle(ref, () => ({
      validateAll: () => {
        return Array.from(refsById.current.entries()).map(([completionId, formActions]) => {
          const result = formActions.validate()
          return { completionId, isValid: result.isValid, answers: result.isValid ? result.answers : null }
        })
      },
    }))

    const registry = useMemo(() => ({ register }), [register])

    return <WorkOrderChecklistRefsContext.Provider value={registry}>{children}</WorkOrderChecklistRefsContext.Provider>
  },
)

export const validateWorkOrderChecklists = async (
  handle: WorkOrderChecklistRefsHandle | null | undefined,
): Promise<boolean> => {
  const results = handle?.validateAll() ?? []
  if (results.some(result => !result.isValid)) {
    toast.error(t('work-orders:checklist-validation-failed'))
    return false
  }

  await Promise.all(
    results
      .filter(result => result.answers != null)
      .map(result => updateChecklistCompletionData({ id: result.completionId, dataJson: result.answers })),
  )
  return true
}
